import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import {
  FormRow,
  type IGroupOption,
  type IOption,
  Select,
  TextField,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type Contact,
  useCreateContactMutation,
  useUpdateContactMutation,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FC, type ReactNode, useMemo } from "react";
import { Form } from "./Form";

type TContact = Pick<
  Contact,
  "id" | "first_name" | "last_name" | "email" | "phone"
>;

type ModalContactPersonProps = {
  title: ReactNode;
  brandsOptions?: IOption[] | IGroupOption[];
  onSubmit?: (contact: TContact) => void;
} & (
  | { contact: TContact; brandId?: undefined; brandsOptions?: undefined }
  | {
      contact?: undefined;
      brandId?: string;
      brandsOptions: IOption[] | IGroupOption[];
    }
  | {
      contact?: undefined;
      brandId: string;
      brandsOptions?: IOption[] | IGroupOption[];
    }
);

export const ModalContactPerson: FC<
  React.PropsWithChildren<ModalContactPersonProps & UseModalProps>
> = ({ title, contact, brandsOptions, brandId, onSubmit, ...modalProps }) => {
  const form = useMemo(() => new Form(contact, brandId), [contact, brandId]);

  const afterCompleted = (contact: TContact) => {
    onSubmit?.(contact);
    modalProps.onClose();
    form.reset();
  };

  const [create, { loading: loadingCreate }] = useCreateContactMutation({
    ...onError(),
    onCompleted(data) {
      if (data?.createContact) {
        afterCompleted(data.createContact);
      }
    },
  });

  const [update, { loading: loadingUpdate }] = useUpdateContactMutation({
    ...onError(),
    onCompleted(data) {
      if (data?.updateContact) {
        afterCompleted(data.updateContact);
      }
    },
  });

  const _onSubmit = async () => {
    const { hasError } = await form.validate(contact ? "update" : "create");
    if (hasError) return;

    if (contact) {
      update({ variables: { input: form.serializeForUpdate(contact.id) } });
    } else {
      create({ variables: { input: form.serializeForCreate() } });
    }
  };

  return (
    <Modal {...modalProps} size="2xl" trapFocus={false}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {brandsOptions && (
            <FormRow title={<Trans>Brand</Trans>}>
              <Select
                placeholder={t`Select brand`}
                {...fieldToSelectProps(form.brand_id, brandsOptions)}
              />
            </FormRow>
          )}

          <FormRow title={<Trans>First name</Trans>}>
            <TextField
              id="field-first-name"
              {...fieldToInputProps(form.first_name)}
            />
          </FormRow>

          <FormRow title={<Trans>Last name</Trans>}>
            <TextField
              id="field-last-name"
              {...fieldToInputProps(form.last_name)}
            />
          </FormRow>

          <FormRow title={<Trans>Email</Trans>}>
            <TextField
              id="field-email"
              placeholder={t`example@web.sk`}
              {...fieldToInputProps(form.email)}
            />
          </FormRow>

          <FormRow title={<Trans>Phone</Trans>}>
            <TextField
              id="field-phone"
              placeholder={t`+421 901 234`}
              {...fieldToInputProps(form.phone)}
            />
          </FormRow>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="grey"
            onClick={modalProps.onClose}
            variant="outline"
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isLoading={loadingCreate || loadingUpdate}
            onClick={_onSubmit}
          >
            <Trans>Save</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalContactPerson);
